import { t } from "i18next"

import { HasGroups, UserData, UserGroup } from "./types"

export type AnonymousDetectableUser = {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  name?: string | null
}

/**
 * Privacy-mode bookings are returned with null identity fields. Detect that
 * shape so UIs can show "Anonymous" / "A" instead of falling back to email.
 */
export function isAnonymousUser(user?: AnonymousDetectableUser | null) {
  if (!user) {
    return false
  }

  const { first_name, last_name, email, name } = user

  if (email === null) {
    return true
  }

  if ((first_name === null && last_name === null) || name === null) {
    return true
  }

  return false
}

export function canOpenColleague(
  user?: AnonymousDetectableUser | null,
): user is AnonymousDetectableUser & { email: string } {
  return !isAnonymousUser(user) && !!user?.email
}

export function formatUser(
  user?: (AnonymousDetectableUser & Pick<UserData, "id" | "profile">) | null,
) {
  if (!user) {
    return t("mobile.general.unknown")
  }

  const { id, first_name, last_name, name, email } = user

  if (isAnonymousUser(user)) {
    return t("general.anonymous")
  }

  if (!!first_name && String(first_name) !== "") {
    if (!!last_name && String(last_name) !== "") {
      return `${first_name} ${last_name}`
    } else {
      return first_name
    }
  } else if (name && String(name) !== "") {
    return name
  } else if (email) {
    return email
  } else if (id) {
    return t("general.employee_no", { id })
  } else {
    return t("mobile.general.unknown")
  }
}

export function isOfficeManager(user?: HasGroups) {
  return user?.groups?.includes(UserGroup.OFFICE_MANAGER) ?? false
}

export function isPortalAdmin(user?: HasGroups & { admin?: boolean }) {
  return (
    (user?.admin === true || user?.groups?.includes(UserGroup.ADMIN)) ?? false
  )
}

export function isNormalUser({ groups = [] }: HasGroups) {
  const hasAdminOrManagerRights =
    groups.includes(UserGroup.ADMIN) ||
    groups.includes(UserGroup.OFFICE_MANAGER)

  return groups.includes(UserGroup.USER) && !hasAdminOrManagerRights
}

export function usernameComparator(a: UserData, b: UserData) {
  if (!a || !b) {
    return 0
  }

  const aName = formatUser(a)
  const bName = formatUser(b)

  return aName.localeCompare(bName)
}
